// YouTube thumbnail extractor
const extractYouTubeThumbnails = (url) => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) throw new Error('Invalid YouTube URL');

  const qualities = [
    { quality: 'maxresdefault', dimensions: '1280x720', url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` },
    { quality: 'hqdefault', dimensions: '480x360', url: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` },
    { quality: 'mqdefault', dimensions: '320x180', url: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` },
    { quality: 'default', dimensions: '120x90', url: `https://img.youtube.com/vi/${videoId}/default.jpg` }
  ];

  return qualities;
};

// Vimeo thumbnail extractor
const extractVimeoThumbnails = async (url) => {
  const videoId = extractVimeoVideoId(url);
  if (!videoId) throw new Error('Invalid Vimeo URL');

  try {
    const response = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`);
    const data = await response.json();
    
    if (data && data[0]) {
      const video = data[0];
      return [
        { quality: 'large', dimensions: '640x360', url: video.thumbnail_large },
        { quality: 'medium', dimensions: '200x150', url: video.thumbnail_medium },
        { quality: 'small', dimensions: '100x75', url: video.thumbnail_small }
      ];
    }
  } catch (error) {
    // Fallback to direct URL method
    return [
      { quality: 'large', dimensions: '640x360', url: `https://vumbnail.com/${videoId}_large.jpg` },
      { quality: 'medium', dimensions: '200x150', url: `https://vumbnail.com/${videoId}_medium.jpg` },
      { quality: 'small', dimensions: '100x75', url: `https://vumbnail.com/${videoId}_small.jpg` }
    ];
  }
  
  throw new Error('Could not extract Vimeo thumbnails');
};

// Dailymotion thumbnail extractor
const extractDailymotionThumbnails = (url) => {
  const videoId = extractDailymotionVideoId(url);
  if (!videoId) throw new Error('Invalid Dailymotion URL');

  return [
    { quality: 'large', dimensions: '480x360', url: `https://www.dailymotion.com/thumbnail/video/${videoId}` },
    { quality: 'medium', dimensions: '320x240', url: `https://www.dailymotion.com/thumbnail/160x120/video/${videoId}` },
    { quality: 'small', dimensions: '120x90', url: `https://www.dailymotion.com/thumbnail/120x90/video/${videoId}` }
  ];
};

// Generic video thumbnail extractor
const extractGenericThumbnails = (url) => {
  // For generic video URLs, we'll try to generate thumbnail URLs
  const baseUrl = url.split('?')[0];
  const extension = baseUrl.split('.').pop().toLowerCase();
  
  if (!['mp4', 'webm', 'ogg', 'avi', 'mov'].includes(extension)) {
    throw new Error('Unsupported video format');
  }

  // Generate potential thumbnail URLs
  const baseName = baseUrl.substring(0, baseUrl.lastIndexOf('.'));
  
  return [
    { quality: 'thumbnail', dimensions: 'Auto', url: `${baseName}_thumb.jpg` },
    { quality: 'poster', dimensions: 'Auto', url: `${baseName}_poster.jpg` },
    { quality: 'preview', dimensions: 'Auto', url: `${baseName}_preview.jpg` }
  ];
};

// Helper functions
const extractYouTubeVideoId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const extractVimeoVideoId = (url) => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
};

const extractDailymotionVideoId = (url) => {
  const match = url.match(/dailymotion\.com\/video\/([^_]+)/);
  return match ? match[1] : null;
};

// Main extraction function
export const extractThumbnails = async (url) => {
  if (!url || typeof url !== 'string') {
    throw new Error('Please provide a valid URL');
  }

  const cleanUrl = url.trim().toLowerCase();

  try {
    if (cleanUrl.includes('youtube.com') || cleanUrl.includes('youtu.be')) {
      return extractYouTubeThumbnails(url);
    } else if (cleanUrl.includes('vimeo.com')) {
      return await extractVimeoThumbnails(url);
    } else if (cleanUrl.includes('dailymotion.com')) {
      return extractDailymotionThumbnails(url);
    } else {
      return extractGenericThumbnails(url);
    }
  } catch (error) {
    throw new Error(error.message || 'Failed to extract thumbnails from this URL');
  }
};

// Validate thumbnail URL
export const validateThumbnailUrl = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    return false;
  }
};